import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Button = ({ onCloseMenu }) => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  const handleLoginClick = () => {
    navigate('/login');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleLogout = async () => {
    try {
      await logout();
      setShowDropdown(false);
      onCloseMenu?.(); // Close hamburger menu
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleMenuItemClick = (action) => {
    setShowDropdown(false);
    onCloseMenu?.(); // Close hamburger menu
    action();
  };

  if (currentUser) {
    return (
      <UserSection ref={dropdownRef}>
        <ProfileIconButton onClick={() => setShowDropdown(!showDropdown)}>
          <ProfileIcon>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </ProfileIcon>
        </ProfileIconButton>
        
        {showDropdown && (
          <>
            <MobileBackdrop onClick={() => setShowDropdown(false)} />
            <UserDropdown>
              <MobileHandle />
            <UserInfo>
              <UserAvatar>
                {(currentUser.displayName || currentUser.firstName) ? 
                  (currentUser.displayName || `${currentUser.firstName} ${currentUser.lastName}`).charAt(0).toUpperCase() : 'U'}
              </UserAvatar>
              <UserDetails>
                <UserDisplayName>
                  {currentUser.displayName || `${currentUser.firstName} ${currentUser.lastName}` || 'User'}
                </UserDisplayName>
                <UserEmail>{currentUser.email}</UserEmail>
              </UserDetails>
            </UserInfo>
            <DropdownDivider />
            <DropdownItem onClick={() => handleMenuItemClick(() => navigate('/profile'))}>
              <ItemIcon>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </ItemIcon>
              Profile Settings
            </DropdownItem>
            <DropdownItem onClick={() => handleMenuItemClick(() => navigate('/orders'))}>
              <ItemIcon>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                </svg>
              </ItemIcon>
              Order History
            </DropdownItem>
            <DropdownItem onClick={() => handleMenuItemClick(() => navigate('/wishlist'))}>
              <ItemIcon>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
              </ItemIcon>
              Liked Products
            </DropdownItem>
            <DropdownItem onClick={() => handleMenuItemClick(() => navigate('/cart'))}>
              <ItemIcon>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
              </ItemIcon>
              Shopping Cart
            </DropdownItem>
            <DropdownDivider />
            <DropdownItem onClick={handleLogout} className="logout">
              <ItemIcon>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16,17 21,12 16,7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
              </ItemIcon>
              Sign Out
            </DropdownItem>
                      </UserDropdown>
          </>
        )}
      </UserSection>
    );
  }

  return (
    <StyledWrapper>
      <button onClick={handleLoginClick}>
        <span className="button_top"> Login </span>
      </button>
    </StyledWrapper>
  );
}

const StyledWrapper = styled.div`
  button {
    /* Variables */
    --button_radius: 0.75em;
    --button_color: #e8e8e8;
    --button_outline_color: #000000;
    font-size: 10px;
    font-weight: bold;
    border: none;
    cursor: pointer;
    border-radius: var(--button_radius);
    background: var(--button_outline_color);
  }

  .button_top {
    display: block;
    box-sizing: border-box;
    border: 2px solid var(--button_outline_color);
    border-radius: var(--button_radius);
    padding: 0.75em 1.5em;
    background: var(--button_color);
    color: var(--button_outline_color);
    transform: translateY(-0.2em);
    transition: transform 0.1s ease;
  }

  button:hover .button_top {
    /* Pull the button upwards when hovered */
    transform: translateY(-0.33em);
  }

  button:active .button_top {
    /* Push the button downwards when pressed */
    transform: translateY(0);
  }
`;

const UserSection = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ProfileIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 48px;
  height: 48px;
  
  &:hover {
    background: #f8f9fa;
    border-color: #111;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
`;

const ProfileIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #111 0%, #333 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const UserName = styled.span`
  color: #333;
  font-weight: 600;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownIcon = styled.span`
  color: #666;
  font-size: 10px;
  transition: transform 0.3s ease;
  margin-left: 4px;
  
  &.open {
    transform: rotate(180deg);
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 12px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  min-width: 280px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0;
    border-radius: 20px 20px 0 0;
    min-width: auto;
    width: 100%;
    max-height: 70vh;
    animation: slideUp 0.3s ease-out;
    box-shadow: 0 -4px 32px rgba(0, 0, 0, 0.15);
    border-bottom: none;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    right: 20px;
    width: 16px;
    height: 16px;
    background: white;
    border: 1px solid #e9ecef;
    border-bottom: none;
    border-right: none;
    transform: rotate(45deg);
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const MobileBackdrop = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    animation: fadeIn 0.3s ease-out;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const MobileHandle = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    width: 40px;
    height: 4px;
    background: #ddd;
    border-radius: 2px;
    margin: 12px auto 8px;
  }
`;

const UserInfo = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  
  @media (max-width: 768px) {
    padding: 16px 20px;
    gap: 16px;
  }
`;

const UserAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #111 0%, #333 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    font-size: 20px;
  }
`;

const UserDetails = styled.div`
  flex: 1;
`;

const UserDisplayName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111;
  margin-bottom: 2px;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 4px;
  }
`;

const UserEmail = styled.div`
  font-size: 14px;
  color: #666;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 14px 20px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  
  &:hover {
    background: #f8f9fa;
    color: #111;
    padding-left: 24px;
  }
  
  &:active {
    background: #e9ecef;
    transform: scale(0.98);
  }
  
  &.logout {
    color: #dc3545;
    border-top: 1px solid #e9ecef;
    
    &:hover {
      background: #fee;
      color: #c53030;
    }
    
    &:active {
      background: #fdd;
    }
  }
  
  @media (max-width: 768px) {
    padding: 18px 20px;
    font-size: 16px;
    gap: 16px;
    min-height: 56px;
    
    &:hover {
      padding-left: 20px;
    }
    
    &:active {
      background: #e9ecef;
      transform: none;
    }
    
    &.logout:active {
      background: #fdd;
    }
  }
`;

const ItemIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    opacity: 0.8;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: #e9ecef;
  margin: 8px 0;
`;

export default Button;
